/**
 * PDP Sticky ATC Bar
 *
 * - Watches the main buy buttons block via IntersectionObserver.
 * - When the buy buttons leave the viewport (scrolled past), the sticky pill
 *   slides in from the bottom (.is-visible).
 * - On first reveal we toggle .is-revealed on the next frame to play the
 *   center→left label slide + price fade-in transition.
 * - Tapping the pill clicks the underlying main ATC button so the existing
 *   selling-plan, properties, and form-submit plumbing all stays intact.
 * - When the buy buttons scroll back into view, the pill slides out and
 *   resets so the playful reveal fires again next time.
 *
 * Mounted via the `pdp-sticky-bar` custom element so it self-initialises
 * whenever the snippet is on the page. Touch-only (CSS hides it >= 700px).
 */

class PdpStickyBar extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    this.trigger = this.querySelector('[data-pdp-sticky-trigger]');
    if (this.trigger) {
      this.trigger.addEventListener('click', this._onClick.bind(this));
    }

    // Slight delay so the rest of the page's hydration / sticky headers
    // don't fight us for initial offset calculations.
    requestAnimationFrame(() => this._observe());
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  _observe() {
    const selector = this.dataset.watchSelector || 'buy-buttons';
    const target = document.querySelector(selector);
    if (!target) {
      // Fallback: if we can't find a target, never show the bar
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            this._hide();
          } else if (entry.boundingClientRect.top < 0) {
            // The buy buttons are above the viewport — show the sticky.
            this._show();
          } else {
            // The buy buttons haven't been reached yet — keep hidden.
            this._hide();
          }
        }
      },
      { threshold: [0, 0.01] }
    );

    this.observer.observe(target);
  }

  _show() {
    if (this.classList.contains('is-visible')) return;
    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');

    // Two requestAnimationFrames: one for the slide-up transform to start,
    // one for the .is-revealed class so the label slide / price fade plays
    // *after* the bar finishes mounting — gives the eye time to catch the
    // initial centered label before it walks left.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-revealed');
      });
    });
  }

  _hide() {
    if (!this.classList.contains('is-visible')) return;
    this.classList.remove('is-visible', 'is-revealed');
    this.setAttribute('aria-hidden', 'true');
  }

  _onClick(event) {
    // Find the main ATC button and click it; this lets the existing form
    // submit + Skio / Rebuy properties + back-in-stock drawer hook all fire
    // through the original buy-buttons code path.
    const candidates = [
      'buy-buttons button[type="submit"]',
      'buy-buttons .button[is="custom-button"]',
      'buy-buttons [aria-controls*="bis-drawer"]',
      '.product-info__buy-buttons button[type="submit"]',
      '[data-pdp-main-cta]',
    ];

    let mainCta = null;
    for (const sel of candidates) {
      mainCta = document.querySelector(sel);
      if (mainCta) break;
    }

    if (mainCta) {
      event.preventDefault();
      mainCta.click();
    }
  }
}

if (!customElements.get('pdp-sticky-bar')) {
  customElements.define('pdp-sticky-bar', PdpStickyBar);
}
